import React, { useState } from 'react';
import {
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const StatusToggleModal = ({ isOpen, onClose, onStatusChanged, job }) => {
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(job?.isActive ?? true);

  if (!isOpen || !job) return null;

  const handleToggleStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/job/status/${job._id}`,
        { isActive: selectedStatus }
      );

      if (response.data.success) {
        onStatusChanged({
          ...job,
          isActive: selectedStatus
        });
        onClose();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                job.isActive 
                  ? 'bg-green-100 text-green-600' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {job.isActive ? (
                  <EyeIcon className="h-6 w-6" />
                ) : (
                  <EyeSlashIcon className="h-6 w-6" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Change Job Status</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Update visibility for: <span className="font-medium">{job.jobType}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Current Status Info */}
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Current Status:</span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                job.isActive 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-200 text-gray-700'
              }`}>
                {job.isActive ? (
                  <>
                    <EyeIcon className="h-4 w-4" />
                    Active
                  </>
                ) : (
                  <>
                    <EyeSlashIcon className="h-4 w-4" />
                    Inactive
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Status Selection */}
          <div className="p-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Select New Status
            </label>
            
            <div className="space-y-3">
              {/* Active Option */}
              <label 
                className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  selectedStatus === true
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="status"
                  checked={selectedStatus === true}
                  onChange={() => setSelectedStatus(true)}
                  className="sr-only"
                />
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      selectedStatus === true ? 'bg-green-500' : 'bg-gray-200'
                    }`}>
                      <EyeIcon className={`h-5 w-5 ${
                        selectedStatus === true ? 'text-white' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Active</p>
                      <p className="text-sm text-gray-500">Job will be visible to users</p>
                    </div>
                  </div>
                  {selectedStatus === true && (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  )}
                </div>
              </label>

              {/* Inactive Option */}
              <label 
                className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  selectedStatus === false
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="status"
                  checked={selectedStatus === false}
                  onChange={() => setSelectedStatus(false)}
                  className="sr-only"
                />
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      selectedStatus === false ? 'bg-red-500' : 'bg-gray-200'
                    }`}>
                      <EyeSlashIcon className={`h-5 w-5 ${
                        selectedStatus === false ? 'text-white' : 'text-gray-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Inactive</p>
                      <p className="text-sm text-gray-500">Job will be hidden from users</p>
                    </div>
                  </div>
                  {selectedStatus === false && (
                    <CheckCircleIcon className="h-6 w-6 text-red-500" />
                  )}
                </div>
              </label>
            </div>

            {/* Warning Message for Inactive */}
            {selectedStatus === false && job.isActive === true && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  Setting this job to inactive will hide it from all user views. Users won't be able to see or apply for this position.
                </p>
              </div>
            )}

            {/* Warning Message for Active */}
            {selectedStatus === true && job.isActive === false && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                <EyeIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700">
                  This job will become visible to users immediately. They will be able to see and apply for this position.
                </p>
              </div>
            )}

            {/* No Change Message */}
            {selectedStatus === job.isActive && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-2">
                <CheckCircleIcon className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600">
                  No status change detected. The job will remain {job.isActive ? 'active' : 'inactive'}.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleToggleStatus}
              disabled={loading || selectedStatus === job.isActive}
              className={`px-4 py-2 rounded-lg font-medium text-white transition-all flex items-center gap-2 ${
                selectedStatus === true
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Updating...
                </>
              ) : (
                <>
                  {selectedStatus === true ? (
                    <>
                      <EyeIcon className="h-4 w-4" />
                      Set Active
                    </>
                  ) : (
                    <>
                      <EyeSlashIcon className="h-4 w-4" />
                      Set Inactive
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusToggleModal;